import { t } from '@lingui/macro';
import { createColumnHelper, Row } from '@tanstack/react-table';
import { isAfter, isBefore, parseISO, startOfDay } from 'date-fns';
import {
  TApplication,
  TApplicationFormalOffer,
} from 'lendingApi/applications.types';
import { toLower } from 'lodash';
import { APPLICATION_STATUS } from 'utils/applications/applications.config';
import { getDurationInMonths } from 'utils/durationHelper';
import { ApplicantCell } from '../Cells/ApplicantCell/ApplicantCell';
import { ApplicationStatusCell } from '../Cells/ApplicationStatusCell/ApplicationStatusCell';
import { ApprovalAmountCell } from '../Cells/ApprovalAmountCell/ApprovalAmountCell';
import { CompanyCell } from '../Cells/CompanyCell/CompanyCell';
import { CreatedCell } from '../Cells/CreatedCell/CreatedCell';
import { ExpiryDateCell } from '../Cells/ExpiryDateCell/ExpiryDateCell';
import { RequestedAmountCell } from '../Cells/RequestedAmountCell/RequestedAmountCell';
import { TermLengthCell } from '../Cells/TermLengthCell/TermLengthCell';

export const COLUMN_IDS = {
  created: 'created',
  companyName: 'companyName',
  applicant: 'applicant',
  status: 'status',
  termLength: 'termLength',
  approvalAmount: 'approvalAmount',
  requestedAmount: 'requestedAmount',
  expiryDate: 'expiryDate',
  referredBy: 'referredBy',
} as const;

export const getDefaultColumns = (showReferredByColumn = false) => {
  const columnHelper = createColumnHelper<TApplication>();

  const filterByName = (
    row: Row<TApplication>,
    columnId: string,
    filterValue?: string,
  ) => {
    if (!filterValue) return true;

    const customer: TApplication['customer'] | TApplication['introducer'] =
      row.getValue(columnId);
    const fullName = `${customer?.first_name ?? ''} ${customer?.last_name ?? ''}`;

    return toLower(fullName).includes(toLower(filterValue));
  };

  const filterByDateRange = (
    row: Row<TApplication>,
    columnId: string,
    filterValue?: [string, string],
  ) => {
    if (!filterValue || (!filterValue[0] && !filterValue[1])) return true;

    const value = row.getValue(columnId);

    if (!value || value === 0 || typeof value !== 'string') return false;

    const rowDate = startOfDay(parseISO(value));
    const fromDate = filterValue[0]
      ? startOfDay(parseISO(filterValue[0]))
      : null;
    const toDate = filterValue[1] ? startOfDay(parseISO(filterValue[1])) : null;

    if (
      (fromDate && isBefore(rowDate, fromDate)) ||
      (toDate && isAfter(rowDate, toDate))
    )
      return false;

    return true;
  };

  const filterByStatus = (
    row: Row<TApplication>,
    columnId: string,
    filterValues?: string[],
  ) => {
    if (!filterValues || filterValues.length === 0) return true;
    const rowVal = row.getValue<string>(columnId);
    return filterValues.includes(rowVal);
  };

  const filterByTermLength = (
    row: Row<TApplication>,
    columnId: string,
    filterValues?: string[],
  ) => {
    if (!filterValues?.length) return true;

    const rowValue = row.getValue<string>(columnId);
    const termLengthValues = rowValue
      .split(',')
      .map((termLength) => termLength.trim());
    return filterValues.some((filterValue) =>
      termLengthValues.includes(filterValue),
    );
  };

  const filterByNumberRange = (
    row: Row<TApplication>,
    columnId: string,
    filterValue?: [string?, string?],
  ) => {
    if (!filterValue || (!filterValue[0] && !filterValue[1])) return true;

    const rowValue = row.getValue(columnId);
    if (rowValue === null || rowValue === '') return false;

    const value = Number(rowValue);
    if (isNaN(value)) return false;

    const min = filterValue[0] ? Number(filterValue[0]) : -Infinity;
    const max = filterValue[1] ? Number(filterValue[1]) : Infinity;

    return value >= min && value <= max;
  };
  // Const to use instead of null or undefined as react-table handles the sorting of them differently
  const SORTABLE_EMPTY_NUMBER_VALUE = -1;

  const columns = [
    columnHelper.accessor('created', {
      header: t`Created`,
      cell: (data) => (
        <CreatedCell
          date={data.getValue()}
          isPartnerChannel={data.row.original.is_partner_channel}
        />
      ),
      id: COLUMN_IDS.created,
      enableHiding: false,
      meta: {
        filterVariant: 'dateRange',
        draggable: false,
      },
      filterFn: filterByDateRange,
    }),
    columnHelper.accessor((row) => row.company?.registered_company_name, {
      cell: (data) => <CompanyCell application={data.row.original} />,
      header: t`Company name`,
      id: COLUMN_IDS.companyName,
      enableHiding: false,
      enableSorting: false,
      minSize: 272,
      meta: {
        draggable: false,
      },
    }),
    columnHelper.accessor('customer', {
      header: t`Applicant`,
      cell: (data) => <ApplicantCell customer={data.getValue()} />,
      id: COLUMN_IDS.applicant,
      enableSorting: false,
      enableHiding: false,
      minSize: 182,
    }),
    columnHelper.accessor('status', {
      header: t`Application status`,
      id: COLUMN_IDS.status,
      enableSorting: false,
      cell: (data) => <ApplicationStatusCell application={data.row.original} />,
      enableHiding: false,
      meta: {
        filterVariant: 'select',
      },
      filterFn: filterByStatus,
      minSize: 232,
    }),
    columnHelper.accessor((row) => getDurations(row), {
      header: t`Term length`,
      cell: (data) => <TermLengthCell termLengths={data.getValue()} />,
      id: COLUMN_IDS.termLength,
      enableSorting: false,
      meta: {
        filterVariant: 'select',
      },
      filterFn: filterByTermLength,
      minSize: 142,
    }),
    columnHelper.accessor(
      (row) => {
        const value =
          row.formal_offer_sets?.[0]?.formal_offers?.[0]?.max_amount;

        // For sorting hidden approval amount values correctly
        return showApprovalAmount(row.status) && value !== null
          ? value
          : SORTABLE_EMPTY_NUMBER_VALUE;
      },
      {
        header: t`Approval amount`,
        cell: (data) => <ApprovalAmountCell application={data.row.original} />,
        id: COLUMN_IDS.approvalAmount,
        meta: {
          filterVariant: 'numberRange',
        },
        filterFn: filterByNumberRange,
        minSize: 132,
      },
    ),
    columnHelper.accessor(
      (row) => {
        // For sorting empty values correctly
        const requestedAmount = row.requested_products?.[0]?.amount;
        return requestedAmount ?? SORTABLE_EMPTY_NUMBER_VALUE;
      },
      {
        header: t`Requested amount`,
        id: COLUMN_IDS.requestedAmount,
        cell: (data) => (
          <RequestedAmountCell
            amount={data.row.original.requested_products?.[0]?.amount}
          />
        ),
        meta: {
          filterVariant: 'numberRange',
        },
        filterFn: filterByNumberRange,
        minSize: 132,
      },
    ),
    columnHelper.accessor(
      (row) => {
        // For sorting hidden timeout values correctly
        const value = row.formal_offer_sets?.[0]?.formal_offers?.[0]?.timeout;
        return showExpiryDate(row.status) && value ? value : 0;
      },
      {
        header: t`Expiry date`,
        id: COLUMN_IDS.expiryDate,
        cell: (data) => (
          <ExpiryDateCell
            date={
              data.row.original.formal_offer_sets?.[0]?.formal_offers?.[0]
                ?.timeout
            }
            application={data.row.original}
          />
        ),
        meta: {
          filterVariant: 'dateRange',
        },
        filterFn: filterByDateRange,
        minSize: 122,
      },
    ),
  ];

  if (showReferredByColumn) {
    columns.push(
      columnHelper.accessor((row) => row.introducer, {
        header: t`Referred by`,
        cell: (data) =>
          `${data.getValue()?.first_name ?? ''} ${data.getValue()?.last_name ?? ''}`,
        id: COLUMN_IDS.referredBy,
        enableSorting: false,
        meta: {
          filterVariant: 'text',
        },
        filterFn: (row, columnId, filterValue) =>
          filterByName(row, columnId, filterValue),
        minSize: 105,
      }),
    );
  }
  return columns;
};

export const showExpiryDate = (status: TApplication['status']) =>
  status === APPLICATION_STATUS.offered ||
  status === APPLICATION_STATUS.conditional_offer ||
  status === APPLICATION_STATUS.talking_to_customer;

export const showApprovalAmount = (status: TApplication['status']) => {
  const showApprovalAmountStatusList = [
    APPLICATION_STATUS.offered,
    APPLICATION_STATUS.conditional_offer,
    APPLICATION_STATUS.funded,
    APPLICATION_STATUS.expired,
    APPLICATION_STATUS.talking_to_customer,
    APPLICATION_STATUS.offer_rejected,
    APPLICATION_STATUS.offer_revoked,
  ];

  return showApprovalAmountStatusList.includes(status);
};

export const getDurations = (data: any) => {
  const formalOffers = data?.formal_offer_sets?.[0]?.formal_offers;
  const hasMultipleFormalOffers = formalOffers?.length > 1;

  const selectedOffer = formalOffers?.find((offer: TApplicationFormalOffer) => {
    return offer.is_selected;
  });

  if (hasMultipleFormalOffers && !selectedOffer) {
    return formalOffers
      .map(({ duration: formalOfferDuration }: TApplicationFormalOffer) => {
        const numberOfMonths = getDurationInMonths(formalOfferDuration);
        return numberOfMonths === 0 ? '-' : t`${numberOfMonths} months`;
      })
      .join(',');
  } else {
    let duration;

    if (selectedOffer) {
      duration = selectedOffer?.duration;
    } else if (formalOffers?.[0]?.duration) {
      duration = formalOffers[0].duration;
    } else {
      duration = data?.requested_products?.[0]?.duration;
    }

    const numberOfMonths = getDurationInMonths(duration);

    return duration && Boolean(numberOfMonths)
      ? t`${numberOfMonths} months`
      : '-';
  }
};
